import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useParticipations, useParticipationMutations } from '../api';
import { useScope } from '../../store/scope';
import { Modal } from '../../components/Modal';
import { PageLoader, ErrorState, StatusBadge, EmptyState } from '../../components/ui';
import { dateRange, num } from '../../lib/format';
import { useToast } from '../../components/Toast';
import { ApiRequestError } from '../../lib/api';
import type { Participation, ParticipationStatus } from '../../lib/types';

const STATUSES: ParticipationStatus[] = ['Planned', 'Confirmed', 'Ongoing', 'Completed', 'Cancelled'];

const empty = {
  expoName: '',
  organizerName: '',
  organizerContact: '',
  venue: '',
  city: '',
  state: '',
  country: 'India',
  startDate: '',
  endDate: '',
  exhibitorNumber: '',
  status: 'Planned' as ParticipationStatus,
  notes: '',
};

export function ParticipationsPage() {
  const { data, isLoading, error, refetch } = useParticipations();
  const { create, update } = useParticipationMutations();
  const setScope = useScope((s) => s.set);
  const toast = useToast();

  const [modal, setModal] = useState<null | { mode: 'create' | 'edit'; row?: Participation }>(null);
  const [form, setForm] = useState(empty);

  const openCreate = () => {
    setForm(empty);
    setModal({ mode: 'create' });
  };
  const openEdit = (row: Participation) => {
    setForm({
      ...empty,
      ...row,
      startDate: row.startDate?.slice(0, 10) ?? '',
      endDate: row.endDate?.slice(0, 10) ?? '',
    });
    setModal({ mode: 'edit', row });
  };

  const save = async () => {
    try {
      if (modal?.mode === 'create') {
        const created = await create.mutateAsync(form);
        toast('Participation created', 'success');
        setScope(created._id);
      } else if (modal?.row) {
        await update.mutateAsync({ id: modal.row._id, body: form });
        toast('Saved', 'success');
      }
      setModal(null);
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Save failed', 'error');
    }
  };

  if (isLoading) return <PageLoader />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">Expo Participations</h1>
          <p className="text-sm text-ink-400">Every exhibition this company exhibits at.</p>
        </div>
        <button className="btn-primary" onClick={openCreate}>
          + New participation
        </button>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No participations yet" action={<button className="btn-primary" onClick={openCreate}>Create the first</button>} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((p) => (
            <div key={p._id} className="card flex flex-col p-4">
              <div className="flex items-start justify-between gap-2">
                <Link to={`/admin/participations/${p._id}`} className="font-serif text-lg font-semibold text-ink-900 hover:underline">
                  {p.expoName}
                </Link>
                <StatusBadge status={p.status} />
              </div>
              <p className="mt-0.5 text-sm text-ink-400">
                {p.organizerName} · {p.city || '—'}
              </p>
              <p className="mt-1 text-xs text-ink-400">{dateRange(p.startDate, p.endDate)}</p>
              <div className="mt-3 flex gap-4 text-xs text-ink-500">
                <span>{num(p.stats?.stallCount ?? 0)} stalls</span>
                <span>{num(p.stats?.rackCount ?? 0)} racks</span>
                <span>{num(p.stats?.assignedProductCount ?? 0)} products</span>
              </div>
              <div className="mt-3 flex gap-2 border-t border-ink-100 pt-3">
                <Link className="btn-outline flex-1" to={`/admin/participations/${p._id}`}>
                  Manage
                </Link>
                <button className="btn-ghost" onClick={() => openEdit(p)}>
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        title={modal?.mode === 'create' ? 'New participation' : 'Edit participation'}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setModal(null)}>
              Cancel
            </button>
            <button className="btn-primary" onClick={save} disabled={create.isPending || update.isPending}>
              Save
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <F label="Expo name *" span>
            <input className="input" value={form.expoName} onChange={(e) => setForm({ ...form, expoName: e.target.value })} />
          </F>
          <F label="Organizer *">
            <input className="input" value={form.organizerName} onChange={(e) => setForm({ ...form, organizerName: e.target.value })} />
          </F>
          <F label="Organizer contact">
            <input className="input" value={form.organizerContact} onChange={(e) => setForm({ ...form, organizerContact: e.target.value })} />
          </F>
          <F label="Venue">
            <input className="input" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
          </F>
          <F label="City">
            <input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </F>
          <F label="State">
            <input className="input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
          </F>
          <F label="Exhibitor #">
            <input className="input" value={form.exhibitorNumber} onChange={(e) => setForm({ ...form, exhibitorNumber: e.target.value })} />
          </F>
          <F label="Start date *">
            <input type="date" className="input" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          </F>
          <F label="End date *">
            <input type="date" className="input" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
          </F>
          <F label="Status">
            <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ParticipationStatus })}>
              {STATUSES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </F>
          <F label="Notes" span>
            <textarea className="input min-h-[64px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </F>
        </div>
      </Modal>
    </div>
  );
}

function F({ label, span, children }: { label: string; span?: boolean; children: React.ReactNode }) {
  return (
    <label className={span ? 'col-span-2 block' : 'block'}>
      <span className="label">{label}</span>
      {children}
    </label>
  );
}
